import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { useFacilityStore } from '../../../../../app/z-store/store';
import * as XLSX from 'xlsx';

const LedgerStatement = () => {
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));
    const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [ledgerData, setLedgerData] = useState(null);
    const [doubleEntries, setDoubleEntries] = useState([]);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [accountsCache, setAccountsCache] = useState({}); // Cache for account details
    const selectedFacilityId = useFacilityStore(state => state.facilityId);
    const toast = useRef(null);

    useEffect(() => {
        if (selectedFacilityId) {
            fetchFinalAccounts();
        }
    }, [selectedFacilityId]);

    useEffect(() => {
        if (selectedAccount && selectedFacilityId) {
            fetchLedgerData();
            fetchDoubleEntries();
        } else {
            setLedgerData(null);
            setDoubleEntries([]);
        }
    }, [selectedAccount, startDate, endDate]);

    // Function to fetch account details by ID
    const fetchAccountById = async (accountId) => {
        // Check cache first
        if (accountsCache[accountId]) {
            return accountsCache[accountId];
        }

        try {
            const response = await makeRequest2(`/api/v1/gl-accounts/${selectedFacilityId}/${accountId}`, 'GET');

            let accountData = null;
            if (response?.data?.data) {
                accountData = response.data.data;
            } else if (response?.data) {
                accountData = response.data;
            }

            if (accountData) {
                // Cache the account data
                setAccountsCache(prev => ({
                    ...prev,
                    [accountId]: accountData
                }));
                return accountData;
            }
        } catch (error) {
            console.error(`Error fetching account ${accountId}:`, error);
        }

        return null;
    };

    // Function to get account display info
    const getAccountDisplayInfo = (accountId) => {
        const account = accountsCache[accountId];
        if (account) {
            return {
                code: account.accountCode,
                name: account.accountName,
                display: `${account.accountCode} - ${account.accountName}`
            };
        }
        return {
            code: 'Loading...',
            name: 'Loading...',
            display: `${accountId.substring(0, 8)}...`
        };
    };

    const fetchFinalAccounts = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(`/api/v1/gl-accounts/final-accounts/${selectedFacilityId}`, 'GET');

            let fetchedAccounts = [];
            if (response?.data?.data) {
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            // Transform accounts for dropdown and cache them
            const formattedAccounts = fetchedAccounts.map(account => {
                // Cache account data
                setAccountsCache(prev => ({
                    ...prev,
                    [account._id]: account
                }));

                return {
                    id: account._id,
                    label: `${account.accountCode} - ${account.accountName}`,
                    value: account._id,
                    type: account.accountType.toLowerCase(),
                    accountBalance: account.accountBalance // Add account balance
                };
            });

            setAccounts(formattedAccounts);
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch accounts'
            });
            console.error('Error fetching accounts:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLedgerData = async () => {
        if (!selectedAccount) return;

        try {
            setLoading(true);

            // Format dates for API request
            const formattedStartDate = startDate.toISOString();
            const formattedEndDate = endDate.toISOString();

            // Fetch ledger entries for selected account
            const facilityId = selectedFacilityId;
            const accountId = selectedAccount;
            const response = await makeRequest2(
                `/api/v1/gl-entries/get-entry-by-account/${facilityId}/${accountId}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
                'GET'
            );

            // Process API response
            let entries = [];
            if (response?.data?.data) {
                entries = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                entries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                entries = response;
            }

            // Get account details
            const account = accounts.find(acc => acc.value === selectedAccount);

            if (entries.length === 0) {
                // Get opening balance from account's current balance for empty entries
                const accountInfo = accounts.find(acc => acc.value === selectedAccount);
                const openingBalance = accountInfo?.accountBalance || 0;

                // Create empty ledger data structure with account info
                setLedgerData({
                    accountCode: account?.label.split(' - ')[0] || '',
                    accountName: account?.label.split(' - ')[1] || '',
                    accountType: account?.type || '',
                    period: {
                        start: startDate.toLocaleDateString(),
                        end: endDate.toLocaleDateString()
                    },
                    openingBalance: openingBalance,
                    endingBalance: openingBalance, // If no transactions, ending = opening
                    totalDebits: 0,
                    totalCredits: 0,
                    transactions: []
                });
            } else {
                // Process entries to create ledger data
                let runningBalance = 0;
                // Sort entries by date
                entries.sort((a, b) => new Date(a.entryDate) - new Date(b.entryDate));

                // Get opening balance from account's current balance
                const accountInfo = accounts.find(acc => acc.value === selectedAccount);
                const openingBalance = accountInfo?.accountBalance || 0;
                runningBalance = openingBalance;

                const transactions = entries.map(entry => {
                    const isDebit = entry.entryType === 'debit';
                    const isCredit = entry.entryType === 'credit';
                    const amount = entry.amount || 0;

                    // For UI display, set debit or credit based on entry type
                    const debitAmount = isDebit ? amount : 0;
                    const creditAmount = isCredit ? amount : 0;

                    // Update running balance based on account type
                    if (account?.type === 'asset' || account?.type === 'expense') {
                        runningBalance = runningBalance + debitAmount - creditAmount;
                    } else {
                        runningBalance = runningBalance - debitAmount + creditAmount;
                    }

                    return {
                        id: entry._id,
                        date: new Date(entry.entryDate),
                        description: entry.description || entry.narration || 'Transaction',
                        reference: entry.reference || entry.referenceNumber || '-',
                        debit: debitAmount,
                        credit: creditAmount,
                        amount: amount,
                        entryType: entry.entryType,
                        balance: amount // Use the transaction amount for balance
                    };
                });

                // Calculate totals
                const totalDebits = transactions.reduce((sum, tx) => sum + tx.debit, 0);
                const totalCredits = transactions.reduce((sum, tx) => sum + tx.credit, 0);

                setLedgerData({
                    accountCode: account?.label.split(' - ')[0] || '',
                    accountName: account?.label.split(' - ')[1] || '',
                    accountType: account?.type || '',
                    period: {
                        start: startDate.toLocaleDateString(),
                        end: endDate.toLocaleDateString()
                    },
                    openingBalance,
                    endingBalance: runningBalance,
                    totalDebits,
                    totalCredits,
                    transactions
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch ledger data'
            });
            console.error('Error fetching ledger data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Enhanced function to fetch double entries with account details
    const fetchDoubleEntries = async () => {
        if (!selectedAccount) return;

        try {
            // Format dates for API request if needed
            const formattedStartDate = startDate.toISOString();
            const formattedEndDate = endDate.toISOString();

            // Fetch double entries for selected account
            const facilityId = selectedFacilityId;
            const accountId = selectedAccount;

            // Using the double entry endpoint 
            const response = await makeRequest2(
                `/api/v1/gl-account-double-entry/get-account-double-entry/${facilityId}/${accountId}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
                'GET'
            );

            console.log('Double entries response:', response);

            // Process API response
            let entries = [];
            if (response?.data?.data) {
                entries = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                entries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                entries = response;
            }

            // Sort entries by date (newest first)
            entries.sort((a, b) => new Date(b.creditEntry.entryDate) - new Date(a.creditEntry.entryDate));

            // Fetch account details for all unique account IDs in the entries
            const uniqueAccountIds = new Set();
            entries.forEach(entry => {
                uniqueAccountIds.add(entry.creditEntry.accountId);
                uniqueAccountIds.add(entry.debitEntry.accountId);
                if (entry.debitEntry.creditAccountId) {
                    uniqueAccountIds.add(entry.debitEntry.creditAccountId);
                }
            });

            // Fetch account details for accounts not in cache
            const accountPromises = Array.from(uniqueAccountIds)
                .filter(accountId => !accountsCache[accountId])
                .map(accountId => fetchAccountById(accountId));

            await Promise.all(accountPromises);

            setDoubleEntries(entries);

            if (entries.length === 0) {
                console.log('No double entry records found');
            }
        } catch (error) {
            console.error('Error fetching double entries:', error);
            setDoubleEntries([]);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const exportToExcel = () => {
        if (!ledgerData || !ledgerData.transactions.length) return;

        const worksheet = XLSX.utils.json_to_sheet(ledgerData.transactions);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");

        XLSX.writeFile(workbook, `Ledger_${ledgerData.accountName}.xlsx`);
    };

    const formatDate = (isoDate) => {
        return new Date(isoDate).toLocaleString();
    };

    const dateTemplate = (rowData) => {
        return new Date(rowData.date).toLocaleDateString();
    };

    // UPDATED: Modified debit template to show green styling
    const debitTemplate = (rowData) => {
        if (rowData.entryType === 'debit') {
            return (
                <div className="d-flex align-items-center justify-content-center" style={{ color: '#28a745' }}>
                    <i className="pi pi-check" style={{ color: '#28a745' }}></i>
                </div>
            );
        }
        return '';
    };

    // UPDATED: Modified credit template to show red styling
    const creditTemplate = (rowData) => {
        if (rowData.entryType === 'credit') {
            return (
                <div className="d-flex align-items-center justify-content-center" style={{ color: '#dc3545' }}>
                    <i className="pi pi-check" style={{ color: '#dc3545' }}></i>
                </div>
            );
        }
        return '';
    };

    // UPDATED: Modified balance template to use green for debits and red for credits
    const balanceTemplate = (rowData) => {
        const className = rowData.entryType === 'debit' ? 'text-success' : 'text-danger';
        return <span className={className}>{formatCurrency(rowData.amount)}</span>;
    };

    // Templates for double entry table
    const doubleEntryDateTemplate = (rowData) => {
        return formatDate(rowData.creditEntry.entryDate);
    };

    const doubleEntryAmountTemplate = (rowData) => {
        return formatCurrency(rowData.creditEntry.amount);
    };

    // UPDATED: Enhanced description template with colored account details
    const doubleEntryDescriptionTemplate = (rowData) => {
        const creditAccount = getAccountDisplayInfo(rowData.creditEntry.accountId);
        const debitAccount = getAccountDisplayInfo(rowData.debitEntry.accountId);

        return (
            <div>
                <div className="mb-2">
                    <strong style={{ color: '#dc3545' }}>Credit:</strong> {rowData.creditEntry.description}
                    <br />
                    <small className="text-muted">Account: <span style={{ color: '#dc3545' }}>{creditAccount.display}</span></small>
                </div>
                <div>
                    <strong style={{ color: '#28a745' }}>Debit:</strong> {rowData.debitEntry.description}
                    <br />
                    <small className="text-muted">Account: <span style={{ color: '#28a745' }}>{debitAccount.display}</span></small>
                </div>
            </div>
        );
    };

    const doubleEntryActionsTemplate = (rowData) => {
        return (
            <div className="text-center">
                <Button
                    label="👁️"
                    className="p-button-rounded p-button-info p-button-sm"
                    onClick={() => showEntryDetails(rowData)}
                    tooltip="View Details"
                    style={{
                        minWidth: '32px',
                        minHeight: '32px',
                        fontSize: '14px'
                    }}
                />
            </div>
        );
    };

    const showEntryDetails = (entry) => {
        setSelectedEntry(entry);
        setShowDetailsDialog(true);
    };

    // UPDATED: Enhanced entry details dialog content with colored account information
    const renderEntryDetailsContent = () => {
        if (!selectedEntry) return null;

        const creditAccount = getAccountDisplayInfo(selectedEntry.creditEntry.accountId);
        const debitAccount = getAccountDisplayInfo(selectedEntry.debitEntry.accountId);
        const creditAccountFromDebit = selectedEntry.debitEntry.creditAccountId ?
            getAccountDisplayInfo(selectedEntry.debitEntry.creditAccountId) : null;

        // Debug log to check account info
        console.log('Credit Account from Debit:', creditAccountFromDebit, 'ID:', selectedEntry.debitEntry.creditAccountId);

        return (
            <div className="transaction-details">
                <div className="transaction-summary mb-4">
                    <div className="card bg-light">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 text-center">
                                    <h4 style={{ color: '#dc3545' }} className="mb-3">Credit Side</h4>
                                    <h5 style={{ color: '#dc3545' }}>{formatCurrency(selectedEntry.creditEntry.amount)}</h5>
                                    <p className="text-muted mb-1" style={{ wordWrap: 'break-word' }}>
                                        {selectedEntry.creditEntry.description}
                                    </p>
                                    <small style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                        {creditAccount.display}
                                    </small>
                                </div>
                                <div className="col-md-6 text-center border-start">
                                    <h4 style={{ color: '#28a745' }} className="mb-3">Debit Side</h4>
                                    <h5 style={{ color: '#28a745' }}>{formatCurrency(selectedEntry.debitEntry.amount)}</h5>
                                    <p className="text-muted mb-1" style={{ wordWrap: 'break-word' }}>
                                        {selectedEntry.debitEntry.description}
                                    </p>
                                    <small style={{ color: '#28a745', fontWeight: 'bold' }}>
                                        {debitAccount.display}
                                    </small>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-12 text-center">
                                    <div className="text-muted">Transaction Date: {formatDate(selectedEntry.creditEntry.entryDate)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-6 mb-3">
                        <div className="card shadow-sm h-100">
                            <div className="card-header" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                                <h5 className="card-title mb-0" style={{ color: '#dc3545' }}>
                                    <i className="pi pi-arrow-circle-down me-2"></i>Credit Entry Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-borderless table-sm">
                                        <tbody>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Entry Date:</th>
                                                <td>{formatDate(selectedEntry.creditEntry.entryDate)}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Amount:</th>
                                                <td style={{ color: '#dc3545', fontWeight: 'bold' }}>{formatCurrency(selectedEntry.creditEntry.amount)}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Type:</th>
                                                <td><span className="badge" style={{ backgroundColor: '#dc3545' }}>{selectedEntry.creditEntry.entryType}</span></td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3 align-top">Description:</th>
                                                <td style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                    {selectedEntry.creditEntry.description}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Account:</th>
                                                <td>
                                                    <div style={{ color: '#dc3545', fontWeight: 'bold' }}>{creditAccount.display}</div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Created At:</th>
                                                <td>{formatDate(selectedEntry.creditEntry.createdAt)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6 mb-3">
                        <div className="card shadow-sm h-100">
                            <div className="card-header" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
                                <h5 className="card-title mb-0" style={{ color: '#28a745' }}>
                                    <i className="pi pi-arrow-circle-up me-2"></i>Debit Entry Details
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-borderless table-sm">
                                        <tbody>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Entry Date:</th>
                                                <td>{formatDate(selectedEntry.debitEntry.entryDate)}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Amount:</th>
                                                <td style={{ color: '#28a745', fontWeight: 'bold' }}>{formatCurrency(selectedEntry.debitEntry.amount)}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Type:</th>
                                                <td><span className="badge" style={{ backgroundColor: '#28a745' }}>{selectedEntry.debitEntry.entryType}</span></td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3 align-top">Description:</th>
                                                <td style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                    {selectedEntry.debitEntry.description}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Account:</th>
                                                <td>
                                                    <div style={{ color: '#28a745', fontWeight: 'bold' }}>{debitAccount.display}</div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Related Credit Account:</th>
                                                <td>
                                                    <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                                                        {creditAccount.display}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-muted text-nowrap pe-3">Created At:</th>
                                                <td>{formatDate(selectedEntry.debitEntry.createdAt)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleExport = () => {
        if (!doubleEntries || doubleEntries.length === 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Export Failed',
                detail: 'No data available to export'
            });
            return;
        }

        try {
            // Create data for the Excel file with account details
            const excelData = doubleEntries.map((entry, index) => {
                const creditAccount = getAccountDisplayInfo(entry.creditEntry.accountId);
                const debitAccount = getAccountDisplayInfo(entry.debitEntry.accountId);

                return {
                    'No.': index + 1,
                    'Date': new Date(entry.creditEntry.entryDate).toLocaleDateString(),
                    'Description': `${entry.creditEntry.description} / ${entry.debitEntry.description}`,
                    'Reference': entry.creditEntry.reference || entry.debitEntry.reference || '-',
                    'Amount': entry.creditEntry.amount,
                    'Credit Account': creditAccount.display,
                    'Debit Account': debitAccount.display,
                    'Entry Type': `${entry.creditEntry.entryType}/${entry.debitEntry.entryType}`,
                    'Created At': new Date(entry.creditEntry.createdAt).toLocaleDateString()
                };
            });

            // Get account info for the file name
            const selectedAccountInfo = accounts.find(acc => acc.value === selectedAccount);
            const accountName = selectedAccountInfo ? selectedAccountInfo.label : 'account';

            // Create a worksheet
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Add title rows (merged cells for header)
            XLSX.utils.sheet_add_aoa(worksheet, [
                [`${accountName} - Double Entry Transactions`],
                [`Period: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`],
                [''] // Empty row for spacing
            ], { origin: 'A1' });

            // Set column widths
            const columnWidths = [
                { wch: 5 },  // No.
                { wch: 12 }, // Date
                { wch: 40 }, // Description
                { wch: 15 }, // Reference
                { wch: 15 }, // Amount
                { wch: 30 }, // Credit Account
                { wch: 30 }, // Debit Account
                { wch: 15 }, // Entry Type
                { wch: 12 }  // Created At
            ];
            worksheet['!cols'] = columnWidths;

            // Create workbook
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Double Entries');

            // Generate Excel file
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

            // Save the file
            const fileName = `${accountName.replace(/[^a-zA-Z0-9]/g, '_')}_double_entries_${new Date().toISOString().split('T')[0]}.xlsx`;
            saveAsExcelFile(excelBuffer, fileName);

            toast.current.show({
                severity: 'success',
                summary: 'Export Successful',
                detail: 'Double entries exported to Excel'
            });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Export Failed',
                detail: 'Failed to export data to Excel'
            });
        }
    };

    const saveAsExcelFile = (buffer, fileName) => {
        const data = new Blob([buffer], { type: 'application/octet-stream' });

        // For IE/Edge
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(data, fileName);
            return;
        }

        // For other browsers
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();

        // Clean up
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
            link.remove();
        }, 100);
    };

    return (
        <div className="ledger-statement-report">
            <Toast ref={toast} />

            <div className="card mb-4">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h4 className="mb-3 mb-md-0">General Ledger</h4>

                        <div className="d-flex flex-wrap gap-2">
                            <div className="p-field">
                                <label htmlFor="start-date" className="font-weight-bold mr-2">From:</label>
                                <Calendar
                                    id="start-date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>

                            <div className="p-field">
                                <label htmlFor="end-date" className="font-weight-bold mr-2">To:</label>
                                <Calendar
                                    id="end-date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="p-field">
                            <label htmlFor="account" className="font-weight-bold d-block mb-2">Select Account:</label>
                            <Dropdown
                                id="account"
                                value={selectedAccount}
                                options={accounts}
                                onChange={(e) => setSelectedAccount(e.value)}
                                placeholder="Select an account to view transactions"
                                className="w-100"
                                filter
                                loading={loading}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            <p className="mt-3">Loading data...</p>
                        </div>
                    ) : (selectedAccount && (ledgerData || doubleEntries.length > 0)) ? (
                        <div>
                            <div className="text-center mb-4">
                                <h5>Account Statement</h5>
                                <h6>{ledgerData?.accountCode || ''} - {ledgerData?.accountName || ''}</h6>
                                <p className="text-muted mb-0">
                                    {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
                                </p>
                            </div>

                            {ledgerData && (
                                <div className="row mb-4">
                                    <div className="col-md-4 mb-3">
                                        <Card className="h-100 shadow-sm">
                                            <div className="text-center">
                                                <h6 className="text-muted">Opening Balance</h6>
                                                <h3 className={ledgerData.openingBalance >= 0 ? 'text-primary' : 'text-danger'}>
                                                    {formatCurrency(Math.abs(ledgerData.openingBalance))}
                                                </h3>
                                                <small className="text-muted">
                                                    {ledgerData.openingBalance >= 0 ? 'Debit' : 'Credit'} Balance
                                                </small>
                                            </div>
                                        </Card>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Card className="h-100 shadow-sm">
                                            <div className="text-center">
                                                <h6 className="text-muted">Activity Summary</h6>
                                                <div className="d-flex justify-content-around">
                                                    <div>
                                                        <div>Debits</div>
                                                        <h5 style={{ color: '#28a745' }}>{formatCurrency(ledgerData.totalDebits)}</h5>
                                                    </div>
                                                    <div>
                                                        <div>Credits</div>
                                                        <h5 style={{ color: '#dc3545' }}>{formatCurrency(ledgerData.totalCredits)}</h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <Card className="h-100 shadow-sm">
                                            <div className="text-center">
                                                <h6 className="text-muted">Ending Balance</h6>
                                                <h3 className={ledgerData.endingBalance >= 0 ? 'text-success' : 'text-danger'}>
                                                    {formatCurrency(Math.abs(ledgerData.endingBalance))}
                                                </h3>
                                                <small className="text-muted">
                                                    {ledgerData.endingBalance >= 0 ? 'Debit' : 'Credit'} Balance
                                                </small>
                                            </div>
                                        </Card>
                                    </div>
                                </div>
                            )}

                            {/* Tab buttons for switching between views */}
                            <div className="mb-3">
                                <ul className="nav nav-tabs" id="myTab" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" id="ledger-tab" data-bs-toggle="tab" data-bs-target="#ledger-tab-pane" type="button" role="tab" aria-controls="ledger-tab-pane" aria-selected="true">
                                            Ledger View
                                        </button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" id="double-entry-tab" data-bs-toggle="tab" data-bs-target="#double-entry-tab-pane" type="button" role="tab" aria-controls="double-entry-tab-pane" aria-selected="false">
                                            Double Entry View
                                        </button>
                                    </li>
                                </ul>
                                <div className="tab-content" id="myTabContent">
                                    {/* Ledger View Tab */}
                                    <div className="tab-pane fade show active" id="ledger-tab-pane" role="tabpanel" aria-labelledby="ledger-tab" tabIndex="0">
                                        {ledgerData && (
                                            <div className="mt-3">
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h5>Ledger Transactions</h5>
                                                    <Button
                                                        label="Export to Excel"
                                                        icon="pi pi-file-excel"
                                                        className="p-button-success p-button-sm"
                                                        onClick={exportToExcel}
                                                    />
                                                </div>
                                                <DataTable
                                                    value={ledgerData.transactions}
                                                    className="p-datatable-sm"
                                                    paginator
                                                    rows={10}
                                                    rowsPerPageOptions={[10, 25, 50]}
                                                    emptyMessage="No transactions found"
                                                >
                                                    <Column field="date" header="Date" body={dateTemplate} sortable style={{ width: '15%' }} />
                                                    <Column field="description" header="Description" sortable style={{ width: '35%' }} />
                                                    <Column field="reference" header="Reference" sortable style={{ width: '15%' }} />
                                                    <Column field="entryType" header="Debit" body={debitTemplate} sortable style={{ width: '10%' }} />
                                                    <Column field="entryType" header="Credit" body={creditTemplate} sortable style={{ width: '10%' }} />
                                                    <Column field="amount" header="Balance" body={balanceTemplate} sortable style={{ width: '15%' }} />
                                                </DataTable>
                                            </div>
                                        )}
                                    </div>

                                    {/* Double Entry View Tab */}
                                    <div className="tab-pane fade" id="double-entry-tab-pane" role="tabpanel" aria-labelledby="double-entry-tab" tabIndex="0">
                                        <div className="mt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h5>Double Entry Transactions</h5>
                                                <Button
                                                    label="Export to Excel"
                                                    icon="pi pi-file-excel"
                                                    className="p-button-success p-button-sm"
                                                    onClick={handleExport}
                                                />
                                            </div>
                                            <DataTable
                                                value={doubleEntries}
                                                className="p-datatable-sm"
                                                paginator
                                                rows={10}
                                                rowsPerPageOptions={[10, 25, 50]}
                                                emptyMessage="No double entry transactions found"
                                            >
                                                <Column header="Date" body={doubleEntryDateTemplate} sortable field="creditEntry.entryDate" style={{ width: '15%' }} />
                                                <Column header="Amount" body={doubleEntryAmountTemplate} sortable field="creditEntry.amount" style={{ width: '15%' }} />
                                                <Column header="Transaction Details" body={doubleEntryDescriptionTemplate} style={{ width: '60%' }} />
                                                <Column body={doubleEntryActionsTemplate} header="Actions" style={{ width: '10%' }} />
                                            </DataTable>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {ledgerData && (
                                <div className="card bg-light mt-4">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-8 offset-md-2">
                                                <table className="table table-borderless">
                                                    <tbody>
                                                        <tr>
                                                            <td className="text-end"><strong>Opening Balance:</strong></td>
                                                            <td className={`text-end ${ledgerData.openingBalance >= 0 ? 'text-primary' : 'text-danger'}`}>
                                                                {formatCurrency(Math.abs(ledgerData.openingBalance))} {ledgerData.openingBalance >= 0 ? 'Dr' : 'Cr'}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-end"><strong>Total Debits:</strong></td>
                                                            <td className="text-end" style={{ color: '#007bff' }}>{formatCurrency(ledgerData.totalDebits)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-end"><strong>Total Credits:</strong></td>
                                                            <td className="text-end" style={{ color: '#dc3545' }}>{formatCurrency(ledgerData.totalCredits)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="text-end border-top"><strong>Ending Balance:</strong></td>
                                                            <td className={`text-end border-top ${ledgerData.endingBalance >= 0 ? 'text-primary' : 'text-success'}`}>
                                                                <strong>{formatCurrency(Math.abs(ledgerData.endingBalance))} {ledgerData.endingBalance >= 0 ? 'Dr' : 'Cr'}</strong>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <p>Select an account to view its transactions</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Transaction Details Dialog */}
            <Dialog
                visible={showDetailsDialog}
                style={{
                    width: 'fit-content',
                    minWidth: '80vw',
                    maxWidth: '95vw'
                }}
                header={
                    <div className="d-flex align-items-center">
                        <i className="pi pi-book me-2" style={{ fontSize: '1.5rem' }}></i>
                        <span className="fs-4">Double Entry Transaction Details</span>
                    </div>
                }
                modal
                className="p-fluid transaction-detail-dialog"
                onHide={() => setShowDetailsDialog(false)}
                footer={
                    <div className="d-flex justify-content-end">
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            onClick={() => setShowDetailsDialog(false)}
                            className="p-button-text"
                        />
                    </div>
                }
                contentClassName="pb-0"
                resizable
                draggable
            >
                {renderEntryDetailsContent()}
            </Dialog>
        </div>
    );
};

export default LedgerStatement;